import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { InputProps, keyframes } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Percent from "_shared/percentages/percent";
import * as Constants from "config";
import React from "react";
import { NumberFormatValues, NumericFormat, SourceInfo } from "react-number-format";
import { Masked } from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ComponentContainer = styled("div")(({ theme }) => ({
  margin: theme.spacing(3),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));
const HighlightTextField = styled(TextField, { shouldForwardProp: (prop) => prop !== "active" })<{ active?: boolean }>(
  ({ active }) => ({
    ...(active && {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          animation: `${pulseBorder} 1.5s ease-in-out infinite`,
        },
      },
    }),
  })
);

const discountDescription =
  "You can share the AttorneyHub fees you incur from a project by establishing " +
  'an agreed upon "discount" from your vendor. 2.912% is the value to cover the cost.';

type DiscountTabProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  isSaved: boolean;
  commentThreads: CommentThread[];
  commentsToolTip: string;
  onCommentsClicked?: (field: ProposalField, title?: string) => void;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function DiscountTab(props: Readonly<DiscountTabProps>) {
  const {
    activeReviewField,
    proposalBuilder,
    disableEditing,
    isSaved,
    commentThreads,
    commentsToolTip,
    onCommentsClicked,
    onProposalBuilderUpdated,
  } = props;

  const [discount, setDiscount] = React.useState(
    Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount) ? 0 : proposalBuilder.currentSpec.workAgreement.discount?.value ?? 0);
  const [isEditing, setIsEditing] = React.useState(false);

  /**
   * Handles the change event for the discount text field
   * @param {NumberFormatValues} values The new values
   * @param {SourceInfo} sourceInfo The source of the change
   */
  async function handleDiscountChange(values: NumberFormatValues, sourceInfo: SourceInfo) {
    if (sourceInfo.source === "prop") return;

    let updatedDiscount = (values.floatValue ?? 0) / 100;
    // Clamp to between 0 and 100%
    if (updatedDiscount < 0) updatedDiscount = 0;
    if (updatedDiscount > 1) updatedDiscount = 1;

    setDiscount(updatedDiscount);
  }

  function renderTextFieldButtons(
    isEditing: boolean,
    setter: (value: React.SetStateAction<boolean>) => void,
    callback: () => void
  ) {
    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount)) return null;

    return (
      <>
        {!disableEditing && !isEditing && (
          <Tooltip title="Edit">
            <span>
              <IconButton
                tabIndex={-1}
                onClick={(event) => {
                  event.stopPropagation();
                  setter(true);
                }}
              >
                <EditIcon color="primary" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {isEditing && (
          <>
            <Tooltip title="Submit">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    callback();
                    setter(false);
                  }}
                >
                  <CheckIcon color="primary" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Cancel">
              <span>
                <IconButton
                  onClick={(event) => {
                    if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount)) return;
                    event.stopPropagation();
                    setter(false);
                    const revertedDiscount = proposalBuilder.currentSpec.workAgreement.discount?.value ?? 0;
                    updateProposalDiscount(revertedDiscount);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </>
    );
  }

  function updateProposalDiscount(discountOverride?: number) {
    onProposalBuilderUpdated(proposalBuilder.setDiscount(new Percent(discountOverride ?? discount)));
  }

  if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount)) {
    return "You do not have permission to view the discount for this proposal.";
  }

  return (
    <ComponentContainer>
      <NumericFormat
        name="discount"
        label="Discount"
        active={Boolean(activeReviewField?.isEqualTo(ProposalField.Discount))}
        customInput={HighlightTextField}
        defaultValue={Constants.defaultDiscount * 100}
        allowNegative={false}
        value={Number(discount * 100)}
        suffix="%"
        type="text"
        thousandSeparator={false}
        decimalScale={2}
        fixedDecimalScale={true}
        disabled={disableEditing}
        isAllowed={(values) => {
          const { floatValue } = values;
          return floatValue == null || (floatValue >= 0 && floatValue <= 100);
        }}
        slotProps={{
          input: {
            endAdornment: (
              <>
                {renderTextFieldButtons(isEditing, setIsEditing, updateProposalDiscount)}
                <CommentsButton
                  commentThreads={commentThreads}
                  disabled={!isSaved}
                  field={ProposalField.Discount}
                  isActive={activeReviewField?.isEqualTo(ProposalField.Discount)}
                  toolTip={commentsToolTip}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(ProposalField.Discount, "Discount");
                        }
                      : undefined
                  }
                />
              </>
            ),
          } as Partial<InputProps> & { active: boolean },
        }}
        onFocus={() => {
          setIsEditing(true);
        }}
        onBlur={() => {
          updateProposalDiscount();
          setIsEditing(false);
        }}
        onValueChange={handleDiscountChange}
        onKeyDown={(event) => {
          if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.discount)) return;

          if (event.key === KeyboardKey.Enter || event.key === KeyboardKey.Tab) {
            updateProposalDiscount();
            setIsEditing(false);
          } else if (event.key === KeyboardKey.Escape) {
            const revertedDiscount = proposalBuilder.currentSpec.workAgreement.discount?.value ?? 0;
            updateProposalDiscount(revertedDiscount);
            setIsEditing(false);
          } else {
            setIsEditing(true);
          }
        }}
      />
      <HelperText>
        A discount of <b>3%</b> will cover subscription costs.
        <Tooltip disableFocusListener title={discountDescription} placement="right" arrow>
          <IconButton size="small">
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </HelperText>
    </ComponentContainer>
  );
}
